import $ from 'jquery';

import CheckoutSectionView from 'chairisher/view/checkout/checkoutsection';
import Disclaimers from 'chairisher/view/product/disclaimers';
import FavoriteProductView from 'chairisher/view/product/favorite';
import QuantityField from 'chairisher/component/form/fieldquantity';
import ShippingOptionField from 'chairisher/component/form/fieldshippingoption';
import TooltipHelper from 'chairisher/view/helper/tooltip';

import {
    doesDatumExist,
    getValueForDatum,
    isTradeInsuranceAvailable,
    renderShippingErrors,
    renderTradeInsurance,
} from 'chairisher/view/helper/checkout';
import {
    trackShippingMethodSelection,
    trackShippingMethodSubmitError,
    trackShippingMethodSubmitSuccess,
} from 'chairisher/analytics/checkout';
import { getPurchaseObject } from 'chairisher/context/purchase';

/**
 * View used to manage product quantity and shipping options in the checkout flow.
 */
class CheckoutProductsView extends CheckoutSectionView {
    constructor(selector) {
        super(selector);
        this.shouldCalculateProcessingFee = false;
    }

    /**
     * @returns {Object|undefined} The value representing all the fields in this view
     */
    getValue() {
        const productData = [];
        this.$fieldsEl.find('.js-product').each((_, el) => {
            const $product = $(el);
            const id = $product.data('product-id');
            const $quantity = $product.find('[name=quantity]');
            const $shippingPartnerCode = $product.find('.js-shipping-partner-code:checked');

            const shouldAttachTradeInsurance = isTradeInsuranceAvailable(
                this.getDataFromContext().find((productObj) => productObj.id?.value === id),
            );
            const productDatum = {
                id: { value: id },
                quantity: { value: $quantity.length ? parseInt($quantity.val(), 10) : 1 },
                shipping_partner_code: { value: $shippingPartnerCode.val() || null },
                is_trade_insurance_attached: { value: shouldAttachTradeInsurance },
            };

            productData.push(productDatum);
        });

        return productData.length > 0 ? { products: productData } : undefined;
    }

    /**
     * @returns {boolean} True when none of the products have a blocking disclaimer.
     */
    areProductsShippable() {
        return this.getDataFromContext().every(
            (product) => getValueForDatum(product.has_blocking_disclaimer) === false,
        );
    }

    /**
     * @inheritDoc
     */
    isValid() {
        const val = this.getValue();
        return (
            val &&
            this.areProductsShippable() &&
            val.products.every(
                (product) =>
                    doesDatumExist(product.id) &&
                    doesDatumExist(product.shipping_partner_code) &&
                    doesDatumExist(product.quantity),
            )
        );
    }

    /**
     * @param productId {string|number}
     * @param selector {string}
     * @return {jQuery}
     */
    getProductDataSelector(productId, selector = '') {
        return this.$fieldsEl.find(`[data-product-id="${productId}"] ${selector}`);
    }

    /**
     * @inheritDoc
     */
    clearErrors() {
        super.clearErrors();
        this.$fieldsEl.find('.js-trade-insurance-container, .js-disclaimer-alerts-errors').removeClass('has-error');
    }

    /**
     * @inheritDoc
     */
    renderForm() {
        const contextData = this.getDataFromContext();
        Disclaimers.render(contextData);
        this.renderShippingOptions();
        renderTradeInsurance(contextData, (id) => this.getProductDataSelector(id, '.js-trade-insurance-container'));
    }

    /**
     * @inheritDoc
     */
    renderErrors() {
        super.renderErrors();
        renderShippingErrors(
            this.getDataFromContext(),
            true,
            (id) => this.getProductDataSelector(id, '.js-shipping-options').closest('.js-form-group'),
            (id) => this.getProductDataSelector(id, '.js-disclaimer-alerts-errors'),
        );
    }

    /**
     * @returns {Array.<Object>}
     */
    getDataFromContext() {
        return getPurchaseObject().products || [];
    }

    /**
     * @inheritdoc
     */
    updateFormData() {}

    /**
     * Binds events to the components in this view
     */
    bind() {
        this.$fieldsEl.find('.js-quantity-field').each((_, el) => {
            const quantityField = new QuantityField({
                $el: $(el),
                callback: () => this.checkoutCallbacks.updateEditableView(this.hasClickedNext),
            });
            quantityField.bind();
        });

        this.$fieldsEl.on('change', '.js-boolean-input', () => {
            // Update this view when any of the inputs change.
            // Only render errors if the user has clicked next.
            // The trade insurance checkboxes default to an invalid state.
            this.toggleNextButtonDisabled(true);
            this.checkoutCallbacks
                .updateEditableView(this.hasClickedNext)
                .always(() => this.toggleNextButtonDisabled(false));
        });

        this.$el.on('click', '.js-product-action-remove, .js-product-action-move-to-favorites', (e) => {
            e.preventDefault();
            const $currentTarget = $(e.currentTarget);
            const $product = $currentTarget.closest('.js-product');
            const productId = $product.data('product-id');
            const $products = $(`.js-product[data-product-id="${productId}"]`);

            if (
                $currentTarget.hasClass('js-product-action-move-to-favorites') &&
                !FavoriteProductView.isProductIdFavorited(productId)
            ) {
                FavoriteProductView.toggleProductId(productId);
                $(document).on('favorite.create', () => {
                    // Wait for an unauthenticated user to sign in through the modal.
                    $products.remove();
                    this.checkoutCallbacks.editStep(this.getStepNumber());
                    this.checkoutCallbacks.updateEditableView(this.hasClickedNext);
                });
            } else {
                $product.collapse('hide').on('hidden.bs.collapse', () => {
                    $products.remove();
                    this.checkoutCallbacks.editStep(this.getStepNumber());
                    this.checkoutCallbacks.updateEditableView(this.hasClickedNext);
                });
            }
        });

        this.$fieldsEl.on('click', '.js-shipping-partner-code', (e) => {
            const $shippingPartnerCode = $(e.currentTarget);
            const $shippingOption = $shippingPartnerCode.closest('.js-shipping-option');
            const $product = $shippingPartnerCode.closest('.js-product');
            const productId = $product.data('product-id');
            const productTaxonomy = $product.data('product-taxonomy');
            const shippingCost = $shippingOption.data('price');
            const shippingType = $shippingOption.data('code');
            const preAdjustedShippingCost = $shippingOption.data('pre-adjusted-price');

            trackShippingMethodSelection(
                productId,
                productTaxonomy,
                shippingCost,
                shippingType,
                preAdjustedShippingCost,
            );
        });
    }

    /**
     * Renders shipping option ui elements for each product
     */
    renderShippingOptions() {
        if (!this.areProductsShippable()) {
            this.$el.find('input').prop('checked', false);
        }

        this.getDataFromContext().forEach((product) => {
            const productId = getValueForDatum(product.id);
            const $product = this.getProductDataSelector(productId);
            const isShippable = getValueForDatum(product.has_blocking_disclaimer) === false;

            const choices = (product.shipping_partner_code && product.shipping_partner_code.choices) || [];
            const shippingPartnerCode = getValueForDatum(product.shipping_partner_code);

            const $shippingOptions = this.getProductDataSelector(productId, '.js-shipping-options');
            $shippingOptions.html(
                choices.map((choice) =>
                    ShippingOptionField.marshal({
                        ...choice,
                        is_checked: shippingPartnerCode && shippingPartnerCode === choice.value,
                        name: `${choice.name || 'shipping-partner-code'}-product-${productId}`,
                        input_classes: 'js-shipping-partner-code',
                    }),
                ),
            );

            if (shippingPartnerCode) {
                $shippingOptions.find(`[value="${shippingPartnerCode}"]`).prop('checked', true);
            }

            $product
                .find('.js-form-group, .js-trade-insurance-container, .js-quantity-choice-container')
                .toggleClass('hidden', !isShippable);
        });
    }

    /**
     * @inheritdoc
     */
    renderSummary() {
        const $tradeInsuranceSummaryItem = $($('#js-template-trade-plus-summary').html());

        const productObjects = this.getDataFromContext().map((product) => {
            const $product = this.getProductDataSelector(getValueForDatum(product.id)).clone();
            $product.find('.quantity-choice-container').remove();
            $product.find('.js-product-actions').remove();
            $product.find('.errorlist').remove();

            // modify title
            if (getValueForDatum(product.quantity) > 1) {
                const $title = $product.find('.js-product-title');
                $title.text(`${$title.text()}(Quantity ${getValueForDatum(product.quantity)})`);
            }

            // modify shipping options
            const $shippingOptions = $product.find('.js-shipping-options');
            $shippingOptions.find('.js-shipping-option').each((_, el) => {
                const $option = $(el);
                if ($option.find('input').is(':checked')) {
                    $option.find('.js-boolean-input').remove();
                } else {
                    $option.remove();
                }
            });

            // ensures the summary shipping options are also populated
            const $summaryShippingOptions = $shippingOptions.clone().addClass('summary-shipping-options');
            $product.find('.js-summary-shipping-options').replaceWith($summaryShippingOptions);

            $product.find('.js-shipping-option-title-tooltip').tooltip({
                placement: TooltipHelper.getPlacement,
            });

            $product.find('.js-trade-insurance-container').remove();
            if (product.is_trade_insurance_attached?.value) {
                const $clone = $tradeInsuranceSummaryItem.clone();
                $product.find('.js-product-meta').append($clone);
            }

            return $product;
        });
        this.$summaryEl.find('.js-products').html(productObjects);
    }

    /**
     * @inheritDoc
     */
    submitErrorAnalytics() {
        trackShippingMethodSubmitError();
    }

    /**
     * @inheritDoc
     */
    submitSuccessAnalytics() {
        trackShippingMethodSubmitSuccess();
    }
}

export default CheckoutProductsView;
